import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { ReportChartComponent } from '@core/reports-ui';
import type { ChatPresenterComponent } from '@core/tellent-assistant-contracts';
import { HeadingComponent } from '@core/user-interface-legacy';

import type { PresentChartArgs } from './present-chart.types';

const CHART_HEIGHT_PX = 280;

const CARTESIAN_SERIES_TYPES = new Set([
  'line',
  'bar',
  'scatter',
  'effectScatter',
  'heatmap',
  'candlestick',
  'boxplot',
]);

const isCartesianOptions = (options: Record<string, unknown>): boolean => {
  const series = options['series'];
  const list = Array.isArray(series) ? series : series ? [series] : [];
  return list.some(
    s =>
      typeof s === 'object' &&
      s !== null &&
      CARTESIAN_SERIES_TYPES.has((<{ type?: string }>s).type ?? ''),
  );
};

@Component({
  selector: 'rt-assistant-present-chart',
  imports: [HeadingComponent, ReportChartComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (title(); as titleText) {
      <rt-heading class="title" variant="heading semi-bold">{{ titleText }}</rt-heading>
    }
    <div class="chart" [style.height.px]="chartHeightPx">
      <rt-report-chart [options]="options()" />
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
      .title {
        margin: 0 0 8px;
      }
      .chart {
        width: 100%;
      }
    `,
  ],
})
export class AssistantPresentChartComponent implements ChatPresenterComponent<PresentChartArgs> {
  protected readonly chartHeightPx = CHART_HEIGHT_PX;
  protected readonly title = computed(() => this.args().title);
  protected readonly options = computed(() => {
    const raw = this.args().options;
    if (isCartesianOptions(raw)) {
      return raw;
    }
    return { ...raw, xAxis: { show: false }, yAxis: { show: false } };
  });

  public readonly args = input.required<PresentChartArgs>();
}
